// Pagamento do boleto agrupado com retenção de imposto (#6003) — a leitura do valor recebido.
//
// O título é registrado pelo valor CHEIO com a retenção como abatimento (ver
// `emissaoBoletoAgrupado.ts`), então o normal é a Prefeitura pagar o líquido: menos que o nominal,
// sem que isso seja pagamento parcial. A baixa antiga dava o mês por pago só porque a data chegou,
// e a diferença retida desaparecia — não havia de onde tirar o total retido para conferir com o
// informe da Prefeitura.
//
// Aqui só a leitura, sem I/O: quem grava é a RPC `registrar_pagamento_fechamento`, que repete estas
// mesmas contas no banco.

export type SituacaoPagamento =
  | 'aguardando'
  | 'pago_integral'
  | 'pago_com_retencao'
  | 'pago_a_menor'
  | 'pago_a_maior';

export interface AvaliacaoPagamento {
  situacao: SituacaoPagamento;
  /** Valor que caiu na conta. */
  recebido: number;
  /** O que era esperado: nominal menos o abatimento. */
  esperado: number;
  /** Quanto a Prefeitura reteve de fato (nominal − recebido), nunca negativo. */
  retido: number;
  /** O que falta em relação ao esperado. Zero quando está em dia. */
  falta: number;
  /**
   * Se o mês pode ser dado como pago e as faturas quitadas. Quando `false` numa correção de valor,
   * a quitação anterior é desfeita — a UC não pode ficar paga num mês que segue sendo cobrado.
   */
  quita: boolean;
  /** Frase para a tela quando algo precisa de olho humano. */
  aviso: string | null;
}

const cent = (v: unknown): number => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? Math.round(n * 100) : 0;
};

const reais = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export interface EntradaAvaliacao {
  /** Valor nominal do título (total do fechamento). */
  bruto: unknown;
  /** Retenção esperada, a mesma que foi ao banco como abatimento. */
  retencao: unknown;
  /** Valor recebido. Nulo ou zero = ainda não pago. */
  recebido: unknown;
  /**
   * Folga em centavos para diferença de arredondamento entre o nosso cálculo e o da Prefeitura.
   * Um centavo por padrão: mais que isso vira decisão de quem conferiu, não regra silenciosa.
   */
  toleranciaCentavos?: number;
}

/**
 * Compara o recebido com o esperado e diz se o mês fecha.
 *
 * A faixa aceita vai do líquido (nominal − retenção) ao nominal: dentro dela o mês é pago, com o
 * valor retido registrado à mostra. Abaixo do líquido é pendência — a diferença fica visível em vez
 * de sumir numa baixa automática. Acima do nominal também fecha, mas com aviso: costuma ser juros e
 * multa por atraso, e quem confere precisa saber que entrou dinheiro a mais.
 */
export function avaliarPagamentoRecebido({
  bruto,
  retencao,
  recebido,
  toleranciaCentavos = 1,
}: EntradaAvaliacao): AvaliacaoPagamento {
  const nominal = Math.max(cent(bruto), 0);
  const ret = Math.min(Math.max(cent(retencao), 0), nominal);
  const pago = Math.max(cent(recebido), 0);
  const esperado = nominal - ret;
  const folga = Math.max(Math.trunc(toleranciaCentavos), 0);
  const base = {
    recebido: pago / 100,
    esperado: esperado / 100,
    retido: Math.max(nominal - pago, 0) / 100,
    falta: Math.max(esperado - pago, 0) / 100,
  };

  if (pago === 0) {
    return { ...base, situacao: 'aguardando', retido: 0, quita: false, aviso: null };
  }
  if (pago > nominal + folga) {
    return {
      ...base,
      situacao: 'pago_a_maior',
      retido: 0,
      quita: true,
      aviso: `Entrou ${reais(pago - nominal)} acima do valor do boleto. Confira se houve juros ou multa antes de fechar o mês.`,
    };
  }
  if (pago >= nominal - folga) {
    return { ...base, situacao: 'pago_integral', retido: 0, quita: true, aviso: null };
  }
  if (pago >= esperado - folga) {
    return { ...base, situacao: 'pago_com_retencao', quita: true, aviso: null };
  }
  return {
    ...base,
    situacao: 'pago_a_menor',
    quita: false,
    aviso: `Faltam ${reais(esperado - pago)} para o valor esperado de ${reais(esperado)}. O mês fica pendente até alguém conferir o comprovante de retenção.`,
  };
}

/** Rótulo da situação para a tela. */
export const ROTULO_SITUACAO_PAGAMENTO: Record<SituacaoPagamento, string> = {
  aguardando: 'Aguardando pagamento',
  pago_integral: 'Pago integral, sem retenção',
  pago_com_retencao: 'Pago com retenção',
  pago_a_menor: 'Pago a menor',
  pago_a_maior: 'Pago acima do boleto',
};
